import type { ChatMessage } from "@/lib/chat-types";

/* ── Helpers ─────────────────────────────────────────────────────── */

function extractTextContent(message: ChatMessage): string {
  return message.blocks
    .filter((block) => block.type === "text")
    .map((block) => block.type === "text" ? block.content : "")
    .join("\n")
    .trim();
}

function extractAttachmentKey(message: ChatMessage): string {
  return message.blocks
    .filter((block) => block.type === "attachment")
    .map((block) => block.type === "attachment" ? block.attachment.filename : "")
    .filter(Boolean)
    .sort()
    .join("|");
}

/**
 * Find the index of an optimistic user message that matches an incoming server-confirmed message.
 *
 * Strategy:
 * 1. Only match messages with `_optimisticId` (client-created messages)
 * 2. Scan forward to find the OLDEST unreconciled optimistic match — since server echoes
 *    arrive in send order, this matches them in the correct order.
 * 3. Require same text content and attachment filenames
 *
 * NOTE: No timestamp window check. Server timestamps may drift from client (Date.now()),
 * so we rely purely on content matching. The `_optimisticId` flag ensures we only match
 * against client-created messages that are still awaiting server confirmation.
 */
function findOptimisticMatch(merged: ChatMessage[], incoming: ChatMessage): number {
  if (incoming.role !== "user") return -1;

  const incomingText = extractTextContent(incoming);
  const incomingAttachments = extractAttachmentKey(incoming);

  for (let i = 0; i < merged.length; i++) {
    const existing = merged[i];
    if (!existing) continue;
    if (existing.role !== "user") continue;
    if (!existing._optimisticId) continue;
    if (extractTextContent(existing) !== incomingText) continue;
    if (extractAttachmentKey(existing) !== incomingAttachments) continue;
    return i;
  }
  return -1;
}

/* ── Public API ──────────────────────────────────────────────────── */

/**
 * Merge incoming messages into the current message list.
 * - Updates existing messages by ID
 * - Reconciles optimistic user messages with server-confirmed ones
 * - Appends genuinely new messages
 * - Sorts by timestamp
 */
export function mergeMessagesById(current: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  if (incoming.length === 0) return current;

  const merged = [...current];
  const existingIds = new Map(current.map((message, index) => [message.id, index]));

  for (const message of incoming) {
    const existingIndex = existingIds.get(message.id);

    if (existingIndex !== undefined) {
      // Update existing message by ID
      merged[existingIndex] = message;
      continue;
    }

    // Try to match an optimistic user message (echo from server)
    const optimisticIndex = findOptimisticMatch(merged, message);
    if (optimisticIndex !== -1) {
      const optimistic = merged[optimisticIndex]!;
      merged[optimisticIndex] = {
        ...message,
        id: optimistic.id,           // preserve stable client ID
        timestamp: optimistic.timestamp, // preserve client timestamp to avoid reorder
        // _optimisticId is dropped (server `message` has no such field)
      };
      continue;
    }

    // Genuinely new message
    existingIds.set(message.id, merged.length);
    merged.push(message);
  }

  merged.sort((a, b) => a.timestamp - b.timestamp);
  return merged;
}
